// Hand-written homepage content: short glosses, word collections and
// practice questions. This is local editorial data, not dictionary API
// output — every word links to its live entry at /define/:word, which is
// where the full, sourced definition comes from.

// Floating cards around the homepage search
export const HERO_CARDS = [
  { word: 'serendipity', partOfSpeech: 'noun', gloss: 'Finding something wonderful by chance.', color: 'bg-butter', tilt: '-rotate-3' },
  { word: 'ethereal', partOfSpeech: 'adjective', gloss: 'So light and delicate it seems otherworldly.', color: 'bg-lavender', tilt: 'rotate-2' },
  { word: 'resilient', partOfSpeech: 'adjective', gloss: 'Able to recover and grow after difficulty.', color: 'bg-mint', tilt: 'rotate-2' },
  { word: 'petrichor', partOfSpeech: 'noun', gloss: 'The earthy smell when rain falls on dry ground.', color: 'bg-peach', tilt: '-rotate-2' },
];

// Shown under the homepage search; each one runs the real search
export const HERO_EXAMPLES = ['serendipity', 'what does resilient mean?', 'petrichor meaning'];

// Questions the search understands, with the word each one looks up
export const ASK_EXAMPLES = [
  'What does petrichor mean?',
  'What’s another word for brave?',
  'How do you use ubiquitous in a sentence?',
  'Define serendipity',
  'melancholy meaning',
  'resilience ko arth',
];

export const CATEGORIES = [
  {
    id: 'beautiful',
    name: 'Beautiful words',
    description: 'Words people love for their sound as much as their meaning.',
    icon: 'Flower2',
    color: 'bg-lavender',
    words: ['serendipity', 'petrichor', 'ethereal', 'mellifluous', 'luminous', 'halcyon'],
  },
  {
    id: 'everyday',
    name: 'Everyday life',
    description: 'Plain, useful words you’ll reach for this week.',
    icon: 'Coffee',
    color: 'bg-mint',
    words: ['errand', 'commute', 'chore', 'leftover', 'cozy', 'budget'],
  },
  {
    id: 'advanced',
    name: 'Advanced vocabulary',
    description: 'For essays, exams and sounding precise.',
    icon: 'GraduationCap',
    color: 'bg-bluebell',
    words: ['perspicacious', 'magnanimous', 'quintessential', 'paradigm', 'ubiquitous', 'juxtaposition'],
  },
  {
    id: 'emotions',
    name: 'Words about emotions',
    description: 'Name exactly how you feel.',
    icon: 'Heart',
    color: 'bg-peach',
    words: ['melancholy', 'elated', 'nostalgia', 'wistful', 'euphoria', 'apprehensive'],
  },
  {
    id: 'unusual',
    name: 'Unusual words',
    description: 'Rare, odd and fun to say out loud.',
    icon: 'Shapes',
    color: 'bg-butter',
    words: ['defenestration', 'sesquipedalian', 'flibbertigibbet', 'kerfuffle', 'lollygag', 'brouhaha'],
  },
  {
    id: 'writers',
    name: 'Words for writers',
    description: 'For drafting, editing and describing things well.',
    icon: 'PenLine',
    color: 'bg-surface',
    words: ['eloquent', 'evocative', 'succinct', 'vivid', 'metaphor', 'narrative'],
  },
  {
    id: 'confused',
    name: 'Commonly confused',
    description: 'Pairs that look or sound alike but mean different things.',
    icon: 'ArrowLeftRight',
    color: 'bg-surface',
    pairs: [
      ['affect', 'effect'],
      ['complement', 'compliment'],
      ['principal', 'principle'],
      ['stationary', 'stationery'],
    ],
  },
  {
    id: 'wisdom',
    name: 'Words of wisdom',
    description: 'Words for good judgement and a calm mind.',
    icon: 'Lightbulb',
    color: 'bg-surface',
    words: ['sagacious', 'prudent', 'discerning', 'judicious', 'equanimity', 'humility'],
  },
];

export const WORTH_KNOWING = [
  { word: 'serendipity', partOfSpeech: 'noun', gloss: 'Finding something good without looking for it.', color: 'bg-peach' },
  { word: 'resilient', partOfSpeech: 'adjective', gloss: 'Able to recover quickly from setbacks.', color: 'bg-surface' },
  { word: 'ubiquitous', partOfSpeech: 'adjective', gloss: 'Seeming to be everywhere at once.', color: 'bg-mint' },
  { word: 'eloquent', partOfSpeech: 'adjective', gloss: 'Expressing ideas clearly and persuasively.', color: 'bg-surface' },
  { word: 'ephemeral', partOfSpeech: 'adjective', gloss: 'Lasting for only a very short time.', color: 'bg-lavender' },
  { word: 'meticulous', partOfSpeech: 'adjective', gloss: 'Very careful about every small detail.', color: 'bg-butter' },
  { word: 'nostalgia', partOfSpeech: 'noun', gloss: 'A fond, slightly sad longing for the past.', color: 'bg-bluebell' },
];

// Practice questions written for the site. `answer` is the index of the
// correct option; the explanation is shown after the visitor answers.
export const CHALLENGES = [
  {
    word: 'meticulous',
    options: ['Careless and in a hurry', 'Showing great attention to detail', 'Very large in size', 'Easily frightened'],
    answer: 1,
    explanation: 'Meticulous means extremely careful and precise. “She kept meticulous notes on every experiment.”',
  },
  {
    word: 'ephemeral',
    options: ['Lasting a very short time', 'Extremely heavy', 'Found only underground', 'Spoken very loudly'],
    answer: 0,
    explanation: 'Something ephemeral is short-lived. “The beauty of the sunset was ephemeral, gone in minutes.”',
  },
  {
    word: 'benevolent',
    options: ['Strict and unforgiving', 'Confused and uncertain', 'Kind and well-meaning', 'Quick to anger'],
    answer: 2,
    explanation: 'A benevolent person wants to do good for others. “A benevolent donor paid for the new library.”',
  },
  {
    word: 'pragmatic',
    options: ['Dreamy and idealistic', 'Dealing with things in a practical, realistic way', 'Related to ancient history', 'Overly emotional'],
    answer: 1,
    explanation: 'Pragmatic means focused on what works in practice. “We took a pragmatic approach and fixed the biggest problem first.”',
  },
  {
    word: 'tenacious',
    options: ['Holding on firmly and refusing to give up', 'Thin and fragile', 'Shy in large groups', 'Lazy and slow'],
    answer: 0,
    explanation: 'A tenacious person keeps going despite difficulty. “Her tenacious effort finally won the scholarship.”',
  },
  {
    word: 'frugal',
    options: ['Spending money freely', 'Careful not to waste money or resources', 'Covered in fruit', 'Fond of travelling'],
    answer: 1,
    explanation: 'Frugal means economical and careful with money. “They lived a frugal life and saved for a house.”',
  },
  {
    word: 'lethargic',
    options: ['Full of energy', 'Easy to understand', 'Sluggish and lacking energy', 'Extremely loud'],
    answer: 2,
    explanation: 'Lethargic describes feeling slow and tired. “The heat made everyone lethargic by the afternoon.”',
  },
  {
    word: 'gregarious',
    options: ['Fond of company and sociable', 'Greedy and selfish', 'Grey in colour', 'Unwilling to change'],
    answer: 0,
    explanation: 'A gregarious person enjoys being with others. “He’s gregarious and talks to everyone at parties.”',
  },
  {
    word: 'eloquent',
    options: ['Unable to speak', 'Fluent and persuasive in speaking or writing', 'Old-fashioned', 'Extremely rich'],
    answer: 1,
    explanation: 'Eloquent means expressing ideas clearly and movingly. “She gave an eloquent speech at graduation.”',
  },
  {
    word: 'ubiquitous',
    options: ['Rare and hard to find', 'Present or found everywhere', 'Very ancient', 'Difficult to pronounce'],
    answer: 1,
    explanation: 'Ubiquitous means seeming to be everywhere. “Smartphones have become ubiquitous.”',
  },
];
